import { useCallback, useMemo } from 'react';

import { DateTools } from '@treezor/design-system';

import {
  CardStatusColor,
  IPaymentCard,
  TCardStatus,
  allcardStatus,
  disabledCardStatus,
} from '@/features/cards/store/models/card.model';

import { cardColor, cardStatusBadgeColor, cardStatusColor } from '@/features/cards/components/CardUI/PaymentCardUI';
import { usePaymentCardType } from '@/features/cards/hooks/usePaymentCardType';

import { useDashboardConfig } from '@/hooks/useDashboardConfig';

export function isDisabledStatus(paymentCardStatus: TCardStatus | undefined): boolean {
  return paymentCardStatus && allcardStatus[paymentCardStatus] ? !!disabledCardStatus[paymentCardStatus] : true;
}

export const usePaymentCardStatus = (paymentCard: IPaymentCard) => {
  const isCardExpired = DateTools.isDateExpired(paymentCard?.expiryDate);
  const { settings } = useDashboardConfig();

  const paymentCardType = usePaymentCardType(paymentCard);

  const paymentCardStatus: TCardStatus = useMemo(() => {
    const cardStatus = allcardStatus[paymentCard.statusCode ?? ''];

    if (
      [
        allcardStatus.DESTROYED,
        allcardStatus.LOST,
        allcardStatus.STOLEN,
        allcardStatus.LOCK_INTERNAL,
        allcardStatus.CUSTOM_LOCK,
      ].includes(cardStatus)
    )
      return cardStatus;
    if (isCardExpired) return allcardStatus.EXPIRED;
    if (paymentCard.isLive && paymentCard.pinTryExceeds && cardStatus === allcardStatus.LOCK)
      return allcardStatus.BLOCKED_PIN_LOCK;
    if (paymentCard.isLive && paymentCard.pinTryExceeds) return allcardStatus.BLOCKED_PIN;
    if (cardStatus === allcardStatus.LOCK) return allcardStatus.LOCK;
    if (!paymentCard.isLive) return allcardStatus.INACTIVE;
    return allcardStatus.ACTIVE;
  }, [isCardExpired, paymentCard.isLive, paymentCard.pinTryExceeds, paymentCard.statusCode]);

  const paymentCardColor = useMemo(() => cardColor[CardStatusColor[paymentCardStatus]], [paymentCardStatus]);

  const paymentCardStatusColor = useMemo(
    () => cardStatusColor[CardStatusColor[paymentCardStatus]],
    [paymentCardStatus],
  );

  const paymentCardStatusBadgeColor = useMemo(
    () => cardStatusBadgeColor[CardStatusColor[paymentCardStatus]],
    [paymentCardStatus],
  );

  const isDisabledPaymentCard = useMemo(() => isDisabledStatus(paymentCardStatus), [paymentCardStatus]);

  const isDisableCardAction = useCallback(() => {
    const cardStatus = allcardStatus[paymentCard.statusCode ?? ''];

    return [
      allcardStatus.DESTROYED,
      allcardStatus.LOST,
      allcardStatus.STOLEN,
      allcardStatus.LOCK_INTERNAL,
      allcardStatus.EXPIRED,
      allcardStatus.BLOCKED_PIN,
      allcardStatus.BLOCKED_PIN_LOCK,
    ].includes(cardStatus);
  }, [paymentCard]);

  const canEditTag = !!settings?.users?.actions ? !settings.users.actions.includes('freezeCardTag') : true;

  return {
    canEditTag,
    paymentCardType,
    status: paymentCardStatus,
    paymentCardColor,
    paymentCardStatusColor,
    paymentCardStatusBadgeColor,
    isCardExpired,
    isDisabledPaymentCard,
    isDisableCardAction,
  };
};
